import "./OrganizationsModal.scss";
import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router";
import { Trans, useTranslation } from "react-i18next";
import {
  Elements,
  useStripe,
  useElements,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { stripePromise } from "../../../../config/stripe";
import { colorOptions, normalizeColor } from "../../../../utils/colors";
import ColorPicker from "../../../ColorPicker/ColorPicker";
import PaymentForm from "../../../PaymentForm/PaymentForm";
import useAuthStore from "../../../../store/authStore";
import {
  updateOrganization,
  createOrganization,
  deleteOrganization,
} from "../../../../api/organizations/organizations";
import { createSetupIntent } from "../../../../api/stripe/stripe";
import EnterpriseOrgModal from "../EnterpriseOrgModal/EnterpriseOrgModal";

function OrganizationsModalContent({ onClose }) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    organizations,
    token,
    fetchOrganizations,
    fetchProjects,
    user,
    refreshUserData,
  } = useAuthStore();
  const [searchText, setSearchText] = useState("");
  const [activePanel, setActivePanel] = useState(null); // 'add', 'edit', 'delete', or 'view'
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [organizationName, setOrganizationName] = useState("");
  const [organizationColor, setOrganizationColor] = useState(colorOptions[0]);
  const [showNameError, setShowNameError] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [allProjects, setAllProjects] = useState([]);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [needsPayment, setNeedsPayment] = useState(false);
  const [paymentStep, setPaymentStep] = useState(false); // true = showing payment form, false = showing org form
  const [cardholderName, setCardholderName] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [paymentMethodId, setPaymentMethodId] = useState(null); // Store tokenized payment method
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const searchInputRef = useRef(null);
  const organizationNameInputRef = useRef(null);
  const previousPanelRef = useRef(null);

  // Get current organization ID from user
  const currentOrganizationId = user?.currentOrganization;

  // Fetch all projects across all organizations when modal opens
  useEffect(() => {
    const fetchAllProjects = async () => {
      const userId = user?._id || user?.id;
      if (!userId || !token) return;

      try {
        // Fetch without organizationId filter to get all projects
        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/projects?userId=${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (res.ok) {
          const projectsData = await res.json();
          setAllProjects(projectsData);
        } else {
          console.error(
            "Failed to fetch projects:",
            res.status,
            res.statusText,
          );
        }
      } catch (error) {
        console.error("Error fetching all projects:", error);
      }
    };

    fetchAllProjects();
  }, [user, token]);

  // Helper function to count projects for an organization
  const getProjectCountForOrg = (orgId) => {
    if (!allProjects || !Array.isArray(allProjects)) {
      return 0;
    }
    const filtered = allProjects.filter((project) => {
      const matches = project.organizationId === orgId;

      return matches;
    });
    return filtered.length;
  };

  // Fetch fresh organizations when modal opens
  useEffect(() => {
    fetchOrganizations(); // Refresh organizations from server
  }, [fetchOrganizations]);

  // Auto-focus search input when modal opens and organizations are loaded
  useEffect(() => {
    // Only focus search if there are organizations
    if (organizations && organizations.length > 0) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [organizations]);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchText("");
  };

  // Filter organizations based on search text
  const filteredOrganizations = (organizations || []).filter((org) =>
    org.name.toLowerCase().includes(searchText.toLowerCase()),
  );

  const handleSelectOrganization = async (orgId) => {
    // Don't do anything if clicking on the already selected organization
    if (orgId === currentOrganizationId) {
      return;
    }

    try {
      // Call backend to switch organization
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/auth/switch-organization`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ organizationId: orgId }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to switch organization");
      }

      const data = await response.json();

      // Update the auth store with new user and currentProject
      const { user: updatedUser, currentProject } = data;

      //   updatedUser,
      //   currentProject,
      // });

      // Merge the updated user fields instead of replacing the entire user object
      // This preserves fields like memberSince, isActive, subscription that aren't returned
      const currentUser = useAuthStore.getState().user;
      useAuthStore.setState({
        user: { ...currentUser, ...updatedUser },
        currentProject: currentProject, // This could be null if no projects exist
      });

      // Fetch projects for the new organization
      await fetchProjects();

      //   "Switched to organization:",
      //   orgId,
      //   "currentProject:",
      //   currentProject
      // );

      // Navigate based on whether the organization has projects
      if (currentProject && location.pathname === "/help") {
        // If switching to an organization with a project and currently on help page, go to tasks
        navigate("/boards");
      } else if (!currentProject && location.pathname !== "/help") {
        // If switching to an organization without projects, go to help page
        navigate("/help");
      }

      // Close the modal after switching
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error("Error switching organization:", error);
    }
  };

  const handleEdit = (orgId) => {
    const org = organizations.find((o) => o._id === orgId);
    setSelectedOrganization(org);
    setOrganizationName(org.name);
    setOrganizationColor(org.color || colorOptions[0]);
    setShowNameError(false);
    setActivePanel("edit");
  };

  const handleDelete = (orgId) => {
    const org = organizations.find((o) => o._id === orgId);
    setSelectedOrganization(org);
    setActivePanel("delete");
  };

  const handleAddOrganization = () => {
    setSelectedOrganization(null);
    setOrganizationName("");
    setOrganizationColor(colorOptions[0]);
    setShowNameError(false);
    setTermsAccepted(false);

    // Skip payment step entirely for free plan users or superAdmin
    if (user?.subscription === "free" || user?.role === "superAdmin") {
      setNeedsPayment(false);
      setPaymentStep(false);
      setActivePanel("add");
      return;
    }

    // Check if user has Stripe customer ID (indicates they've set up payment before)
    const hasPaymentMethod = !!user?.stripeCustomerId;

    setNeedsPayment(!hasPaymentMethod);
    setPaymentStep(!hasPaymentMethod);

    setActivePanel("add");
  };

  const handleClosePanel = () => {
    setActivePanel(null);
    setSelectedOrganization(null);
    setOrganizationName("");
    setOrganizationColor(colorOptions[0]);
    setShowNameError(false);
    setTermsAccepted(false);
    setNeedsPayment(false);
    setPaymentStep(false);
    setPaymentMethodId(null); // Clear stored payment method
    setCardholderName("");
    setCardComplete(false);
  };

  const handleProceedToOrganization = async () => {
    // Validate payment info
    if (!stripe || !elements) {
      console.error("Stripe is not ready");
      return;
    }

    if (!cardComplete) {
      console.error("Please complete your card details");
      return;
    }

    if (!cardholderName.trim()) {
      console.error("Please enter cardholder name");
      return;
    }

    // Use SetupIntent for 3D Secure authentication (required for EU/Danish cards)
    try {
      // Step 1: Create SetupIntent on backend
      const { clientSecret } = await createSetupIntent(token);

      // Step 2: Confirm card setup (triggers 3D Secure/MitID if needed)
      const cardElement = elements.getElement(CardNumberElement);
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: cardholderName,
              email: user.email,
            },
          },
        },
      );

      if (stripeError) {
        console.error(
          "[OrganizationsModal] Card setup failed:",
          stripeError.message,
        );
        return;
      }

      // Step 3: Store the payment method ID (now properly authenticated)
      setPaymentMethodId(setupIntent.payment_method);

      // Move to organization form
      setPaymentStep(false);
    } catch (error) {
      console.error("[OrganizationsModal] Error during card setup:", error);
    }
  };

  const handleSaveOrganization = async () => {
    if (!organizationName.trim()) {
      setShowNameError(true);
      return;
    }

    // Validate payment fields if needed
    if (needsPayment && activePanel === "add") {
      if (!stripe || !elements) {
        console.error("Stripe is not ready");
        return;
      }

      if (!cardComplete) {
        console.error("Please complete your card details");
        return;
      }

      if (!cardholderName.trim()) {
        console.error("Please enter cardholder name");
        return;
      }
    }

    if (activePanel === "edit" && selectedOrganization) {
      // Update existing organization
      try {
        const updateData = {
          name: organizationName.trim(),
          color: organizationColor,
        };
        await updateOrganization(selectedOrganization._id, updateData, token);
        // Refresh organizations list
        await fetchOrganizations();
        handleClosePanel();
      } catch (error) {
        console.error("Failed to update organization:", error);
        // You might want to show an error message to the user
      }
    } else if (activePanel === "add") {
      // Create new organization
      try {
        const newOrgData = {
          name: organizationName.trim(),
          color: organizationColor,
        };

        // If user needed payment and we have a stored payment method ID, include it
        if (needsPayment && paymentMethodId) {
          newOrgData.payment = {
            paymentMethodId: paymentMethodId,
            cardName: cardholderName,
          };
        }

        const newOrg = await createOrganization(newOrgData, token);

        // Refresh user data to get updated billing status (hasPaymentMethod, etc.)
        await refreshUserData();

        // Refresh organizations list
        await fetchOrganizations();

        // Select the newly created organization
        if (newOrg && newOrg._id) {
          await handleSelectOrganization(newOrg._id);
        }

        // Close the modal
        onClose();
      } catch (error) {
        console.error("Failed to create organization:", error);
        // You might want to show an error message to the user
      }
    }
  };

  const handleDeleteOrganization = async () => {
    try {
      const isDeletingCurrentOrganization =
        currentOrganizationId === selectedOrganization._id;

      await deleteOrganization(selectedOrganization._id, token);

      // Refresh user data to get updated billing status (subscription cancelled, etc.)
      await refreshUserData();

      // Refresh organizations list
      await fetchOrganizations();
      handleClosePanel();

      // If we deleted the current organization, we need to reload user data and redirect
      if (isDeletingCurrentOrganization) {
        // Clear the current project since it belonged to the deleted organization
        const { setCurrentProject } = useAuthStore.getState();
        setCurrentProject(null);

        // Close the modal first
        onClose();
        // Redirect to help page
        navigate("/help");
        // Trigger a full auth check to update the user's state
        // This will set a new current organization if available
        window.location.reload();
      }
    } catch (error) {
      console.error("Error deleting organization:", error);
      // TODO: Show error message to user
    }
  };

  // Auto-focus organization name input when add/edit panel opens and content is ready
  useEffect(() => {
    if (
      (activePanel === "add" || activePanel === "edit") &&
      contentReady &&
      organizationNameInputRef.current
    ) {
      // Small delay to ensure the input is fully rendered and focusable
      const timer = setTimeout(() => {
        organizationNameInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [activePanel, contentReady]);

  // Handle delayed content rendering for smooth animation
  useEffect(() => {
    const wasExpanded = previousPanelRef.current !== null;
    const isExpanding = !wasExpanded && activePanel !== null;

    if (isExpanding) {
      setContentReady(false);
      const timer = setTimeout(() => {
        setContentReady(true);
      }, 300);
      previousPanelRef.current = activePanel;
      return () => clearTimeout(timer);
    } else if (activePanel) {
      setContentReady(true);
      previousPanelRef.current = activePanel;
    } else {
      setContentReady(false);
      previousPanelRef.current = null;
    }
  }, [activePanel]);

  const getPanelTitle = () => {
    switch (activePanel) {
      case "add":
        return t("organizations.addNew");
      case "edit":
        return (
          <Trans
            i18nKey="organizations.editTitle"
            values={{
              name:
                selectedOrganization?.name || t("organizations.organization"),
            }}
            components={{
              name: (
                <span
                  style={{
                    color: selectedOrganization
                      ? normalizeColor(selectedOrganization.color)
                      : "inherit",
                  }}
                />
              ),
            }}
          />
        );
      case "delete":
        return t("organizations.removeTitle");
      case "view":
        return t("organizations.detailsTitle");
      default:
        return "";
    }
  };

  return (
    <div
      className={`organizations-modal-content ${activePanel ? "expanded" : ""}`}
    >
      <div className="title">
        <span>{t("organizations.title")}</span>
        <button className="close-button" onClick={onClose}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      <div className="main-wrapper">
        <div className="left">
          {/* Show search only if there are organizations */}
          {organizations && organizations.length > 0 && (
            <div className="search-container">
              <input
                ref={searchInputRef}
                type="text"
                className="search-input"
                placeholder={t("organizations.searchPlaceholder")}
                value={searchText}
                onChange={handleSearchChange}
              />
              <span
                className="material-symbols-outlined search-icon"
                style={searchText ? { cursor: "pointer" } : {}}
                onClick={searchText ? handleClearSearch : undefined}
              >
                {searchText ? "close" : "search"}
              </span>
            </div>
          )}

          <div className="organization-list">
            {/* Show big centered buttons if no organizations exist */}
            {organizations && organizations.length === 0 ? (
              <div className="empty-state-container">
                {/* Show regular org button for individual billing users */}
                {user?.billingType !== "invoice" && (
                  <button
                    className="add-first-organization-button"
                    onClick={handleAddOrganization}
                  >
                    <span className="material-symbols-outlined">add</span>
                    <span>{t("organizations.addFirst")}</span>
                  </button>
                )}
                {/* Show enterprise button only for invoice billing users */}
                {user?.billingType === "invoice" && (
                  <button
                    className="add-first-enterprise-button"
                    onClick={() => setShowEnterpriseModal(true)}
                  >
                    <span className="material-symbols-outlined">business</span>
                    <span>{t("organizations.createEnterprise")}</span>
                  </button>
                )}
              </div>
            ) : filteredOrganizations.length === 0 ? (
              <div
                style={{
                  padding: "2rem",
                  textAlign: "center",
                  color: "var(--task-not-started-color)",
                  fontSize: "0.9rem",
                }}
              >
                {searchText
                  ? t("organizations.noMatch")
                  : t("organizations.empty")}
              </div>
            ) : (
              filteredOrganizations.map((org) => {
                // Check if user is admin in this organization
                const userId = user?._id || user?.id;
                const isOrgAdmin =
                  org.members?.find((m) => String(m.userId) === String(userId))
                    ?.role === "admin";

                return (
                  <div
                    key={org._id}
                    className={`organization-item ${org._id === currentOrganizationId ? "current" : ""} ${selectedOrganization?._id === org._id ? "selected" : ""}`}
                    onClick={() => handleSelectOrganization(org._id)}
                    style={{
                      cursor:
                        org._id === currentOrganizationId
                          ? "default"
                          : "pointer",
                    }}
                  >
                    <div
                      className="organization-color"
                      style={{
                        backgroundColor: normalizeColor(org.color),
                      }}
                    ></div>
                    <div className="organization-info">
                      <div className="organization-name">{org.name}</div>
                      <div className="organization-stats">
                        <span className="organization-projects">
                          {t("organizations.projectCount", {
                            count: getProjectCountForOrg(org._id),
                          })}
                        </span>
                        <span className="stat-separator">•</span>
                        <span className="organization-members">
                          {t("organizations.memberCount", {
                            count: org.members?.length || 0,
                          })}
                        </span>
                      </div>
                    </div>
                    {isOrgAdmin && (
                      <div className="organization-actions">
                        <button
                          className="action-button edit-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(org._id);
                          }}
                          title={t("organizations.editTooltip")}
                        >
                          <span className="material-symbols-outlined">
                            edit
                          </span>
                        </button>
                        <button
                          className="action-button delete-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(org._id);
                          }}
                          title={t("organizations.removeTooltip")}
                        >
                          <span className="material-symbols-outlined">
                            delete
                          </span>
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Show add buttons only if there are existing organizations */}
          {organizations && organizations.length > 0 && (
            <div className="add-buttons-container">
              {/* Show regular org button only for individual billing users */}
              {user?.billingType !== "invoice" && (
                <button
                  className="add-organization-button"
                  onClick={handleAddOrganization}
                >
                  <span className="material-symbols-outlined">add</span>
                  {t("organizations.addNew")}
                </button>
              )}
              {/* Show enterprise org button only for invoice billing users */}
              {user?.billingType === "invoice" && (
                <button
                  className="add-enterprise-button"
                  onClick={() => setShowEnterpriseModal(true)}
                  title={t("organizations.createEnterpriseTooltip")}
                >
                  <span className="material-symbols-outlined">business</span>
                  {t("organizations.createEnterpriseShort")}
                </button>
              )}
            </div>
          )}
        </div>

        <div className={`right ${activePanel ? "active" : ""}`}>
          <div className="panel-header">
            <h3 className="panel-title">{getPanelTitle()}</h3>
            <button className="close-panel-button" onClick={handleClosePanel}>
              <span className="material-symbols-outlined">arrow_back</span>
            </button>
          </div>

          <div className="panel-content">
            {contentReady && activePanel === "view" && selectedOrganization && (
              <div className="form-content">
                <div className="form-section">
                  <div className="form-label">{t("common.name")}</div>
                  <div className="org-detail">{selectedOrganization.name}</div>
                </div>
                <div className="form-section">
                  <div className="form-label">
                    {t("organizations.membersLabel")}
                  </div>
                  <div className="org-detail">
                    {t("organizations.memberCount", {
                      count: selectedOrganization.members?.length || 0,
                    })}
                  </div>
                </div>
                <div className="form-section">
                  <div className="form-label">
                    {t("organizations.projectsLabel")}
                  </div>
                  <div className="org-detail">
                    {t("organizations.projectCount", {
                      count: getProjectCountForOrg(selectedOrganization._id),
                    })}
                  </div>
                </div>
                <div className="form-section">
                  <div className="form-label">{t("common.color")}</div>
                  <div
                    className="color-preview"
                    style={{
                      backgroundColor: normalizeColor(
                        selectedOrganization.color,
                      ),
                    }}
                  ></div>
                </div>
              </div>
            )}{" "}
            {contentReady && activePanel === "add" && paymentStep && (
              <div className="form-content">
                <div className="form-section">
                  <div className="form-label">
                    {t("organizations.paymentInfo")}
                  </div>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "var(--text-faint-color)",
                      marginBottom: "16px",
                    }}
                  >
                    {t("organizations.paymentInfoHint")}
                  </p>
                  <input
                    type="text"
                    className="form-input"
                    placeholder={t("organizations.cardholderName")}
                    value={cardholderName}
                    onChange={(e) => setCardholderName(e.target.value)}
                    style={{ marginBottom: "0px", height: "44px" }}
                  />
                  <PaymentForm
                    onCardChange={(event) => {
                      setCardComplete(event.complete);
                    }}
                  />
                </div>

                <div className="form-section" style={{ marginTop: "20px" }}>
                  <div
                    style={{
                      fontSize: "13px",
                      lineHeight: "1.6",
                      color: "var(--text-faint-color)",
                      marginBottom: "12px",
                    }}
                  >
                    {t("organizations.payOnlyHint")}
                  </div>
                </div>
              </div>
            )}
            {contentReady && activePanel === "add" && !paymentStep && (
              <div className="form-content">
                <div className="form-section">
                  <div className="form-label">
                    {t("organizations.nameLabelExample")}
                  </div>
                  <input
                    ref={organizationNameInputRef}
                    type="text"
                    className="form-input"
                    placeholder={t("organizations.namePlaceholder")}
                    value={organizationName}
                    onChange={(e) => {
                      setOrganizationName(e.target.value);
                      if (showNameError && e.target.value.trim()) {
                        setShowNameError(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSaveOrganization();
                      }
                    }}
                  />
                  {showNameError && (
                    <div className="form-error">
                      {t("organizations.nameRequired")}
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <div className="form-label">{t("common.color")}</div>
                  <div className="color-picker-wrapper">
                    <ColorPicker
                      selectedColor={organizationColor}
                      onColorSelect={setOrganizationColor}
                    />
                  </div>
                </div>

                {user?.subscription !== "free" &&
                  user?.role !== "superAdmin" && (
                    <div className="form-section" style={{ marginTop: "20px" }}>
                      <div
                        style={{
                          fontSize: "13px",
                          lineHeight: "1.6",
                          color: "var(--text-faint-color)",
                          marginBottom: "12px",
                        }}
                      >
                        {user?.subscription === "trial"
                          ? t("organizations.billingHintTrial")
                          : t("organizations.billingHint")}
                      </div>

                      <label
                        style={{
                          display: "flex",
                          alignItems: "flex-start",
                          cursor: "pointer",
                          gap: "8px",
                          fontSize: "13px",
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                          style={{
                            marginTop: "2px",
                            cursor: "pointer",
                            width: "14px",
                            height: "14px",
                            flexShrink: 0,
                          }}
                        />
                        <span style={{ color: "var(--text-dim-color)" }}>
                          {user?.subscription === "trial"
                            ? t("organizations.billingConfirmTrial")
                            : t("organizations.billingConfirm")}
                        </span>
                      </label>
                    </div>
                  )}
              </div>
            )}
            {contentReady && activePanel === "edit" && selectedOrganization && (
              <div className="form-content">
                <div className="form-section">
                  <div className="form-label">{t("common.name")}</div>
                  <input
                    ref={organizationNameInputRef}
                    type="text"
                    className="form-input"
                    placeholder={t("organizations.namePlaceholder")}
                    value={organizationName}
                    onChange={(e) => {
                      setOrganizationName(e.target.value);
                      if (showNameError && e.target.value.trim()) {
                        setShowNameError(false);
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleSaveOrganization();
                      }
                    }}
                  />
                  {showNameError && (
                    <div className="form-error">
                      {t("organizations.nameRequired")}
                    </div>
                  )}
                </div>

                <div className="form-section">
                  <div className="form-label">{t("common.color")}</div>
                  <div className="color-picker-wrapper">
                    <ColorPicker
                      selectedColor={organizationColor}
                      onColorSelect={setOrganizationColor}
                    />
                  </div>
                </div>
              </div>
            )}
            {contentReady &&
              activePanel === "delete" &&
              selectedOrganization && (
                <div className="form-content">
                  <p>
                    {t("organizations.removeConfirm", {
                      name: selectedOrganization.name,
                    })}
                  </p>
                </div>
              )}
          </div>

          <div className="panel-footer">
            {contentReady && activePanel === "add" && paymentStep && (
              <div className="form-buttons">
                <button className="cancel-button" onClick={handleClosePanel}>
                  {t("common.cancel")}
                </button>
                <button
                  className="save-button"
                  onClick={handleProceedToOrganization}
                  disabled={!cardComplete || !cardholderName.trim()}
                  style={{
                    opacity: cardComplete && cardholderName.trim() ? 1 : 0.5,
                    cursor:
                      cardComplete && cardholderName.trim()
                        ? "pointer"
                        : "not-allowed",
                  }}
                >
                  {t("common.proceed")}
                </button>
              </div>
            )}
            {contentReady && activePanel === "add" && !paymentStep && (
              <div className="form-buttons">
                <button className="cancel-button" onClick={handleClosePanel}>
                  {t("common.cancel")}
                </button>
                <button
                  className="save-button"
                  onClick={handleSaveOrganization}
                  disabled={
                    user?.subscription !== "free" &&
                    user?.role !== "superAdmin" &&
                    !termsAccepted
                  }
                  style={{
                    opacity:
                      user?.subscription === "free" ||
                      user?.role === "superAdmin" ||
                      termsAccepted
                        ? 1
                        : 0.5,
                    cursor:
                      user?.subscription === "free" ||
                      user?.role === "superAdmin" ||
                      termsAccepted
                        ? "pointer"
                        : "not-allowed",
                  }}
                >
                  {t("common.create")}
                </button>
              </div>
            )}

            {contentReady && activePanel === "edit" && (
              <div className="form-buttons">
                <button className="cancel-button" onClick={handleClosePanel}>
                  {t("common.cancel")}
                </button>
                <button
                  className="save-button"
                  onClick={handleSaveOrganization}
                >
                  {t("common.save")}
                </button>
              </div>
            )}

            {contentReady && activePanel === "delete" && (
              <div className="delete-actions">
                <button
                  className="confirm-delete-button"
                  onClick={handleDeleteOrganization}
                >
                  {t("common.remove")}
                </button>
                <button className="cancel-button" onClick={handleClosePanel}>
                  {t("common.cancel")}
                </button>
              </div>
            )}

            {contentReady && activePanel === "view" && (
              <div className="form-buttons">
                <button className="cancel-button" onClick={handleClosePanel}>
                  {t("common.close")}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Enterprise Organization Modal */}
      {showEnterpriseModal && (
        <EnterpriseOrgModal
          onClose={() => {
            setShowEnterpriseModal(false);
            fetchOrganizations();
            refreshUserData();
          }}
        />
      )}
    </div>
  );
}

// Wrap with Stripe Elements provider
export default function OrganizationsModal({ onClose }) {
  return (
    <Elements stripe={stripePromise}>
      <OrganizationsModalContent onClose={onClose} />
    </Elements>
  );
}
